import { useEffect } from "react";

function isVisible(el: HTMLElement): boolean {
  if (el.getAttribute("aria-hidden") === "true") return false;
  if (el.closest('[aria-hidden="true"]')) return false;
  // `offsetParent` is null for `display:none` and many hidden cases.
  if (el.offsetParent === null && getComputedStyle(el).position !== "fixed") return false;
  return true;
}

function getScopeContainer(start: HTMLElement): HTMLElement {
  return (
    (start.closest("[data-enter-next-scope=\"true\"]") as HTMLElement | null) ??
    (start.closest("form") as HTMLElement | null) ??
    (start.closest("[role=\"dialog\"]") as HTMLElement | null) ??
    document.body
  );
}

function getFocusableWithin(container: HTMLElement): HTMLElement[] {
  const nodes = Array.from(
    container.querySelectorAll<HTMLElement>(
      [
        'input:not([type="hidden"]):not([disabled])',
        "select:not([disabled])",
        "textarea:not([disabled])",
        "button:not([disabled])",
        '[tabindex]:not([tabindex="-1"])',
      ].join(",")
    )
  );

  return nodes.filter((el) => {
    if (!isVisible(el)) return false;
    // Skip disabled via aria
    if (el.getAttribute("aria-disabled") === "true") return false;
    // Skip elements inside a disabled fieldset
    if (el.closest("fieldset[disabled]")) return false;
    return true;
  });
}

function focusNextFieldWithin(container: HTMLElement, current: HTMLElement) {
  const focusables = getFocusableWithin(container);
  if (focusables.length === 0) return;
  const idx = focusables.indexOf(current);
  const next = idx >= 0 ? focusables[idx + 1] : focusables[0];
  next?.focus();
}

function shouldHandleEnter(target: HTMLElement): boolean {
  if (target.isContentEditable) return false;
  if (target instanceof HTMLTextAreaElement) return false;

  if (target instanceof HTMLInputElement) {
    const t = (target.type || "").toLowerCase();
    if (["checkbox", "radio", "button", "submit", "file", "image", "reset"].includes(t)) {
      return false;
    }
    // Let native "Enter" submit only when explicitly allowed
    if (target.closest('[data-enter-next-ignore="true"]')) return false;
    return true;
  }

  if (target instanceof HTMLSelectElement) {
    if (target.closest('[data-enter-next-ignore="true"]')) return false;
    return true;
  }

  // Default: do not hijack Enter for buttons/links/custom triggers
  return false;
}

/**
 * Global "Enter focuses next field" behavior.
 * - Skips textareas (keeps multi-line).
 * - Skips buttons/links/custom triggers (keeps expected Enter behavior).
 * - Scopes to nearest form/dialog (so focus doesn't jump across the page).
 * - Opt-out per subtree with `data-enter-next-ignore="true"`.
 */
export default function EnterKeyNextField() {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Enter") return;
      if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.defaultPrevented) return;

      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (!shouldHandleEnter(target)) return;

      const container = getScopeContainer(target);
      e.preventDefault();
      focusNextFieldWithin(container, target);
    };

    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, []);

  return null;
}

